import type { FirmaResumenDTO } from "../components/enrollment/firmaEstado";
/** Sign-up (matrícula por etapas) — Fase 0 data shapes returned by the v2 API. */

export const ENROLLMENT_NOTE_TYPE = {
	ALTA: 1,
	ACTUALIZACION: 2,
	SISTEMA: 3
} as const;

export interface EnrollmentAdvisorDTO {
	/** Zoho CRM user id (string) */
	userId: string;
	name: string;
	email?: string | null;
	sharePercentage: number | null;
}

/** What the UI sends when creating/updating advisors. */
export interface AdvisorSelection {
	userId: string;
	name: string;
	sharePercentage: number | null;
}

export interface AdvisorCandidateDTO {
	id: string;
	name: string;
	email: string | null;
	roles: string[];
}

export interface EnrollmentNoteDTO {
	id: number;
	typeId: number;
	type: string;
	note: string;
	createdAt: string | null;
	createdBy: string | null;
}

export interface EnrollmentAttendanceSummaryDTO {
	total: number;
	present: number;
	absent: number;
	rate: number | null;
}

export interface EnrollmentHistoryDTO {
	id: number;
	campusId: number;
	isCurrentCampus: boolean;
	statusId: number;
	status: string;
	courseId: number | null;
	/** Course of the enrollment (what bills): "Hotelería" for a composite course. */
	courseName: string;
	levelId: number | null;
	levelName: string;
	scheduleId: number | null;
	scheduleName: string;
	/** Course of the current schedule ("Gastronomía"); differs from `courseName` on composite courses. */
	scheduleCourseName?: string;
	scheduleCode: string;
	scheduleStatusId: number | null;
	scheduleStatus: string;
	periodName: string;
	startDate: string | null;
	endDate: string | null;
	sessions: string;
	mentorName: string;
	mentorFirstName: string;
	mentorLastName: string;
	classroomName: string;
	buildingName: string;
	createdAt: string | null;
	createdBy: string | null;
	updatedAt: string | null;
	updatedBy: string | null;
	commercialTerms: string | null;
	advisors: EnrollmentAdvisorDTO[];
	notes: EnrollmentNoteDTO[];
	attendance: EnrollmentAttendanceSummaryDTO;
}

export interface AvailableScheduleKitDTO {
	id: number;
	name: string;
	sku: string;
	basePrice: number;
}

export interface AvailableScheduleDTO {
	id: number;
	name: string;
	code: string;
	statusId: number;
	status: string;
	eligibility: "NUEVO" | "REINGRESO";
	courseId: number | null;
	courseName: string;
	levelId: number | null;
	levelName: string;
	periodName: string;
	startDate: string;
	endDate: string;
	billableMonths: number;
	sessions: string;
	mentorName: string;
	mentorFirstName: string;
	mentorLastName: string;
	enrolledStudents: number;
	studentsLimit: number;
	isFull: boolean;
	subscriptionPrice: number | null;
	monthlyBillingPrice: number | null;
	/** Lo que de verdad se cobra en este horario: plan del curso según la fecha en que arranca (historial de precios) + sobrecargo. */
	coursePlan?: { hasPlan: boolean; source: string | null; billableMonths: number | null; monthlyPrice: number | null; message?: string };
	currencySymbol?: string;
	kits: AvailableScheduleKitDTO[];
	/** Curso compuesto: the course is defined by blocks. Composite courses have no schedules of their own,
	 *  so this is only informational here; enrolling into one goes through `/courses/_composite`. */
	isComposite: boolean;
}

export interface StudentDocumentDTO {
	id: number;
	typeId: number;
	type: string;
	name: string;
	mimeType: string | null;
	size: number | null;
	description: string | null;
	createdAt: string | null;
	createdBy: string | null;
	/** Only for contracts generated by WAPP at enrollment: the signed copy is uploaded right here. */
	contract?: { id: number; signed: boolean; signedAt: string | null; generatedAt?: string | null; canUploadSigned: boolean; versions?: ContractVersionDTO[]; whatsappProspectId?: number | null; firma?: FirmaResumenDTO | null } | null;
}

export interface StudentDocumentsResponseDTO {
	types: { [typeId: string]: string };
	documents: StudentDocumentDTO[];
}

export interface StudentAuditChangeDTO {
	field: string;
	label: string;
	old: string | null;
	new: string | null;
}

export interface StudentAuditEntryDTO {
	id: number;
	campusId: number | null;
	entityType: "student" | "enrollment" | "document" | string;
	entityId: number | null;
	action: string;
	summary: string;
	changes: StudentAuditChangeDTO[];
	createdAt: string | null;
	createdBy: string;
}

/* ---------- Fase 1: Solicitudes de matrícula ---------- */

export const REQUEST_STATUS = { BORRADOR: 1, ENVIADA: 2, DEVUELTA: 3, APROBADA: 4, CANCELADA: 5 } as const;
export const REQUEST_MODE = { REMOTA: 1, PRESENCIAL: 2 } as const;
export const PAYMENT_INTENT_TYPE = { DEPOSITO: 1, LINK: 2, EN_CAMINO: 3 } as const;
export const PAYMENT_INTENT_STATUS = { PENDIENTE: 1, APLICADO: 2, RECHAZADO: 3, PROCESADO: 4 } as const;
export const REQUEST_DOCUMENT_TYPE = { COMPROBANTE: 1, ID_ALUMNO: 2, ID_TUTOR: 3, OTRO: 4 } as const;

export interface RequestScheduleDTO {
	scheduleId: number; notes: string; name: string; code: string; statusId: number | null;
	courseName: string; levelName: string; periodName: string; startDate: string | null; endDate: string | null;
	sessions: string; subscriptionPrice: number | null; monthlyBillingPrice: number | null;
	/** Diplomado por bloques (Hotelería, Belleza General): el horario es el de su bloque de arranque. */
	courseId?: number | null; diplomadoName?: string; blockName?: string | null; billableMonths?: number | null;
	/** Inglés en paralelo, incluido en el diplomado. */
	inglesScheduleId?: number | null; inglesScheduleName?: string | null; inglesSessions?: string | null;
	/** Rotativa: otros horarios del mismo módulo y periodo donde también toma clase. */
	rotativo?: boolean; rotativoScheduleIds?: number[]; rotativoSchedules?: { id: number; name: string; sessions: string }[];
	/** Eximir meses (reingreso / nivelación de Inglés): se aplica al aprobar. */
	eximir?: { months: string[]; reason: string; explanation: string } | null;
}
/** GET enrollmentRequests/_rotativos?scheduleId= */
export interface RotativaOptionDTO { id: number; name: string; sessions: string; weeklyHours: number; mentorName: string | null; enrolled: number; limit: number; }
export interface RotativaOptionsDTO { home: RotativaOptionDTO | null; options: RotativaOptionDTO[]; }
/** GET enrollmentRequests/_diplomados: diplomados por bloques del plantel, cada bloque con los horarios que empiezan. */
export interface DiplomadoScheduleDTO {
	id: number; name: string; code: string; levelId: number; periodName: string | null; startDate: string; endDate: string;
	classesGiven: number; sessions: string; mentorName: string | null; enrolled: number; limit: number;
	/** Días y horas (week_day, start/end en décimas de hora) para no elegir Inglés a la misma hora que el bloque. */
	slots: { day: number; start: number; end: number }[];
	/** Precio del diplomado si arranca en este horario (historial de precios); null en Inglés en paralelo. */
	monthlyPrice?: number | null; billableMonths?: number | null;
}
export interface DiplomadoBlockDTO {
	id: number; name: string; kindId: number; sessions: number; targetLevelName: string | null; targetCourseName: string | null;
	schedules: DiplomadoScheduleDTO[];
}
export interface DiplomadoDTO {
	id: number; name: string; code: string; currencySymbol?: string;
	billing: { billableMonths: number; monthlyPrice: number; subscriptionPrice: number } | null;
	blocks: DiplomadoBlockDTO[];
}
export interface RequestPaymentDTO {
	typeId: number | null;
	notes?: string | null;
	amount: number | null;
	recipientAccountId: number | null;
	paymentMethodId: number | null;
	reference: string | null;
	paidAt: string | null;
}

export interface RequestStudentData {
	firstName?: string; lastName?: string; identificationTypeId?: number | string; identificationCode?: string; birthday?: string;
	genderId?: number | string; nationalityId?: number | string; countryId?: number | string; stateId?: number | string | null; cityId?: number | string | null;
	stateName?: string; cityName?: string; email?: string; phone1?: string; phone2?: string; address?: string; zipcode?: string;
	academicDegreeId?: number | string; lastSchoolName?: string; currentOccupation?: string; observations?: string;
	tagIds?: number[]; contacts?: { name: string; relation: string; contactMedia: string }[]; links?: { facebook?: string; instagram?: string };
}

export interface EnrollmentRequestSummaryDTO {
	id: number; campusId: number; modeId: number; mode: string; statusId: number; status: string;
	/** Matrícula grupal (misma bundle_key, kind «personas») o el mismo alumno con varias matrículas del día (kind «alumno»). */
	group?: { key: string; kind?: "personas" | "alumno"; size: number; others: { id: number; name: string; statusId: number }[] } | null;
	/** Quién de Caja tiene el ticket (2026-09-25). */
	cajaShort?: string | null;
	studentId: number | null; isNewStudent: boolean; studentName: string; studentGenderId?: number | null; identificationCode: string | null; phone: string | null;
	courseName: string; levelName: string; scheduleName: string; scheduleId: number | null; scheduleCount?: number;
	/** Talleres y Personalizados (2026-10-24): cuántos lleva y, si no tiene horario, el nombre del primero. */
	workshopCount?: number; workshopName?: string | null;
	advisors: EnrollmentAdvisorDTO[]; advisorsShort?: string[]; paymentTypeId: number | null; paymentAmount: number | null;
	assignedToId: number | null; assignedTo: string | null; assignedToShort?: string | null;
	createdById: number; createdBy: string | null; createdByShort?: string | null;
	createdAt: string | null; updatedAt: string | null; version?: number | null; submittedAt: string | null; enrollmentId: number | null; paymentIntentId: number | null;
	/** Devuelta por Caja: el alumno ya está matriculado y solo falta reponer el comprobante. */
	returnedByCaja?: boolean;
}

export interface AttachmentDTO { index: number; name: string; mime: string | null; }
export interface RequestCommentDTO { id: number; typeId: number; body: string; createdAt: string | null; createdBy: string; attachments?: AttachmentDTO[]; }
export interface RequestDocumentDTO { id: number; typeId: number; type: string; name: string; mimeType: string | null; size: number | null; createdAt: string | null; createdBy: string | null;
	comment?: string | null; rejected?: boolean; rejectedAt?: string | null; rejectedBy?: string | null; rejectionReason?: string | null; sentToCaja?: boolean; }
export interface ExistingStudentDTO { id: number; name: string; identityCode: string | null; identificationCode: string | null; phone1: string | null; email: string | null; statusId?: number | null; isActive?: boolean; match?: string;
	/** Su ficha, para confirmarla o corregirla en la solicitud de reingreso (2026-09-25). */
	studentData?: any; }

/** Una versión del contrato (generado o firmado); las reemplazadas quedan con current=false. */
export interface ContractVersionDTO {
	id: number;
	typeId: number;          // 1 generado, 2 firmado
	type: string;
	name: string;
	createdAt: string | null;
	createdBy: string | null;
	current: boolean;
	discardedAt: string | null;
	discardedBy: string | null;
	discardReason: string | null;
}

export interface RequestContractDTO { id: number; enrollmentId: number | null; course: string; statusId: number; status: string; name: string | null; error: string | null; missingGuardian: boolean; generatedAt: string | null;
	signed?: boolean; signedName?: string | null; signedAt?: string | null; signedBy?: string | null; canUploadSigned?: boolean;
	offered?: string; offeredEditable?: boolean;
	versions?: ContractVersionDTO[]; firma?: FirmaResumenDTO | null; }

/** Un taller o personalizado que se puede vender en la solicitud (Académico › Talleres, programado). */
export interface SellableWorkshopDTO {
	id: number; name: string; status: string; courses: { id: number; name: string }[];
	startDate: string; endDate: string; days: number; hours: number; durationText: string; timesText: string;
	price: number; currencySymbol: string; capacity: number | null; spotsLeft: number | null;
	/** Precio para alumnos matriculados (talleres HN: L 200 menos); null = sin descuento. */
	priceStudent?: number | null;
}
/** Lo elegido en la solicitud. price vacío = el del taller; uno distinto solo lo ponen los roles de dinero, con motivo. */
export interface RequestWorkshopPick { workshopId: number; notes: string; price?: number | null; priceNote?: string | null; }
export interface RequestWorkshopRowDTO extends RequestWorkshopPick { priceByName?: string | null; finalPrice: number | null; workshop: SellableWorkshopDTO | null; }

export interface EnrollmentRequestDetailsDTO extends EnrollmentRequestSummaryDTO {
	studentData: RequestStudentData; notes: string | null; payment: RequestPaymentDTO | {};
	schedules: RequestScheduleDTO[];
	workshops?: RequestWorkshopRowDTO[];
	workshopAttendeeIds?: number[];
	cancelReason: string | null; reviewedBy: string | null; reviewedByShort?: string | null; reviewedAt: string | null;
	schedule: { id: number; name: string; code: string; statusId: number; courseName: string; levelName: string; periodName: string; startDate: string; endDate: string; sessions: string; subscriptionPrice: number | null; monthlyBillingPrice: number | null } | null;
	existingStudent: ExistingStudentDTO | null;
	comments: RequestCommentDTO[]; documents: RequestDocumentDTO[];
	/** Contratos que WAPP genera al matricular, uno por matrícula (2026-09-25). statusId: 0 generando, 1 listo, 2 falló, 3 en el canal. */
	contracts?: RequestContractDTO[];
	paymentIntent: { id: number; statusId: number; status: string; paymentId: number | null } | null;
	validation: string[]; duplicates: ExistingStudentDTO[];
	/** Paso Promoción (Etapa 3 de cobros): lo elegido por curso, el "¿con quién?" y el referido. null = solicitud de antes. */
	promotion?: RequestPromotionDTO | null;
	/** Ya aprobada: { scheduleId: matrícula } (Corregir Promoción). */
	enrollmentBySchedule?: Record<number, number>;
	/** Las otras personas que se matriculan junto con esta (se aprueban juntas, un solo ticket). */
	bundle?: { id: number; name: string; statusId: number; status: string; schedules: string[]; assignedTo: string | null }[];
	/** Ya aprobada: los acuerdos comerciales de cada matrícula con su tira de meses. */
	agreements?: AgreementStripDTO[];
	/** Supervisora / Dirección de Admisiones (o Supervisión de Caja): puede «Aprobar de todos modos» un multimes vencido. */
	canApproveAdvanceException?: boolean;
	guardian?: { isMinor: boolean; options: { index: number; name: string; relation: string; contact: string }[] };
	permissions: { isRevisor: boolean; isSupervisora: boolean; canManageReviewers: boolean; canPresencial: boolean; canCreate: boolean; canReadAll: boolean;
		isDireccionVentas?: boolean; isAsesoraAdmisiones?: boolean; readOnly?: boolean; canMarkError?: boolean; canDiscard?: boolean; canSendToError?: boolean; canCloseProcessed?: boolean; submitApproves?: boolean; canEdit: boolean; canSubmit: boolean; canReview: boolean; canResendToCaja?: boolean; canChangeSchedule?: boolean; canChangePromotion?: boolean;
		reassignOptions?: { userId: number; name: string; hint: string }[] };
}

export interface RequestPromotionLinkDTO { type: "solicitud" | "matricula" | "grupo" | "nuevo_grupo" | "alumno"; requestId?: number; scheduleId?: number; enrollmentId?: number; groupId?: number; studentId?: number; regroup?: "todos" | "conservar"; }
/** Excepción de promoción (ESPEC_AUDITORIA_PREGUNTAS B): la pide quien llena (reason) y la aprueba Supervisión / Dirección de Admisiones. */
export interface RequestPromotionExceptionDTO { promotionId?: number; reason: string; approve?: boolean; requestedBy?: string; requestedAt?: string; approvedBy?: string; approvedByUserId?: number; approvedAt?: string;
	/** Pedida por WappTeam (2026-11-02): la aprobación, a quién se pidió y, si la rechazaron, quién y por qué. */
	aprobacionId?: number; askedTo?: string[]; rejectedBy?: string; rejectedReason?: string; rejectedAt?: string; }
export interface RequestPromotionItemDTO { scheduleId: number; promotionId: number | null; inscriptionPromotionId?: number | null; link?: RequestPromotionLinkDTO | null; exception?: RequestPromotionExceptionDTO | null; }
/** referredBy: quién lo refirió, dicho una sola vez (2026-10-27): la persona (alumno o graduado); enrollmentId solo en solicitudes de antes. */
export interface RequestPromotionDTO { items: RequestPromotionItemDTO[]; referredBy?: { studentId?: number; enrollmentId?: number } | null; override10?: { by?: string; reason: string; at?: string } | null;
	/** «Va a Precio de Lista»: la nota habla de un descuento pero de verdad va sin promoción de mensualidades (2026-11-02). */
	noneConfirmed?: boolean; }
export interface AgreementMonthDTO { tuitionId: number; n: number; title: string; state: string; listAmount: number; subTotal: number; paidAmount: number; dueDate: string | null; agreementState: string | null; target: number | null; applied: string | null; note: string | null; }
export interface AgreementStripDTO { enrollmentId?: number; agreements: { id: number; name: string; code: string; statusId: number; status: string; combineGroup: string; role: string | null; conditionText: string | null; groupId: number | null; members: { name: string; role: string; enrollmentId: number }[]; requestId: number | null; createdAt: string; blocked?: string | null; exception?: { reason: string; at: string } | null }[]; months: AgreementMonthDTO[]; }

export interface EnrollmentRequestListDTO {
	data: EnrollmentRequestSummaryDTO[]; total: number; perPage: number; currentPage: number; lastPage: number;
	permissions: { isRevisor: boolean; isSupervisora: boolean; canManageReviewers: boolean; canPresencial: boolean; canCreate: boolean; canReadAll: boolean;
		isDireccionVentas?: boolean; isAsesoraAdmisiones?: boolean };
}

export interface RequestCatalogsDTO { statuses: Record<string, string>; modes: Record<string, string>; paymentTypes: Record<string, string>; documentTypes: Record<string, string>; }
export interface ReviewerDTO { userId: number; name: string; email: string | null; active?: boolean; assignedCount?: number; lastAssignedAt?: string | null; }
export interface ReviewersDTO { pool: ReviewerDTO[]; candidates: ReviewerDTO[]; canManage: boolean; }

export interface PaymentIntentSummaryDTO {
	/** Matrícula grupal: las otras personas del ticket. Varios depósitos: fotos vigentes y cuántas ya tienen su pago. */
	groupNames?: string[]; photosTotal?: number; photosRegistered?: number;
	/** "kit" = pago digital de producto reportado desde Catálogo; "matricula" = solicitud aprobada. */
	kind?: "kit" | "matricula"; concept?: string | null; kitIds?: number[];
	id: number; campusId: number; requestId: number | null; typeId: number; type: string; statusId: number; status: string;
	studentId: number | null; studentName: string; identityCode: string | null; enrollmentId: number | null; courseName: string; levelName: string; scheduleName: string;
	amount: number | null; recipientAccountId: number | null; paymentMethodId: number | null; reference: string | null; paidAt: string | null;
	hasDocument: boolean; documentName: string | null; documents: IntentDocumentDTO[]; notes: string | null; paymentId: number | null; invoiceId: number | null; resolutionNotes: string | null;
	createdBy: string | null; createdByShort?: string | null; createdAt: string | null; resolvedBy: string | null; resolvedAt: string | null;
	/** A quién de Caja le tocó por turno; quién de Ventas lo mandó y quién de Admisiones lo aprobó (2026-09-25). */
	assignedToShort?: string | null; salesShort?: string | null; admissionsShort?: string | null;
	/** "Reporta tu pago" (/pagar, 2026-10-10): lo envió el alumno; studentId puede venir vacío si el teléfono no dio con nadie. */
	fromStudent?: boolean; contactName?: string | null; contactPhone?: string | null; contactEmail?: string | null; contactCountry?: string | null;
	/** Solicitud de Promoción (2026-10-31): la asesora pidió la promo con su comprobante (source 2); nunca es «lo envió el alumno». */
	promoRequest?: boolean; sourceName?: string | null;
}
export interface IntentDocumentDTO { index: number; name: string; comment?: string | null; uploadedAt?: string | null; uploadedBy?: string | null;
	rejected?: boolean; rejectedAt?: string | null; rejectedBy?: string | null; rejectionReason?: string | null; }
export interface IntentHistoryDTO { type: "creado" | "rechazado" | "reenviado" | "procesado" | string; at: string | null; by: string | null; text: string; docs: string[]; attachments: AttachmentDTO[]; }
export interface PaymentIntentTuitionDTO { id: number; number: number; title: string; typeId: number; statusId: number; subTotal: number; paidAmount: number; pending: number; dueDate: string; }
export interface PaymentIntentDetailsDTO extends PaymentIntentSummaryDTO {
	studentPhone: string | null; history?: IntentHistoryDTO[]; tuitions: PaymentIntentTuitionDTO[]; suggestedTuitionIds: number[]; suggestedTotal: number;
	articles?: { id: number; title: string; name: string; quantity: number; statusId: number; subTotal: number; paidAmount: number; pending: number }[]; suggestedArticleIds?: number[];
	permissions: { isCaja: boolean; canApply: boolean; canProcess: boolean };
	/** Borrador del modal "Registrar pago" guardado en el ticket (mientras espera una aprobación). */
	draft?: any; draftAt?: string | null;
}
export interface PaymentIntentListDTO { data: PaymentIntentSummaryDTO[]; total: number; perPage: number; currentPage: number; lastPage: number; permissions: { isCaja: boolean }; statuses: Record<string, string>; types: Record<string, string>; }

/** Rechazada por Caja → Enviar a Error: qué se anula y qué la frena (vacío = se puede). */
export interface ErrorPreviewDTO {
	/** Todas las personas del ticket; se eligen las que van a Error (las demás siguen y sus descuentos se ajustan solos). */
	ticket: { id: number; name: string; isThis: boolean }[];
	requests: { id: number; name: string }[];
	enrollmentIds: number[];
	agreements: string[];
	affectedOthers: string[];
	blockers: string[];
}

/** Rechazada por Caja → Cerrar como Ya Procesada: factura real (no anulada, con dinero) que pagó meses de la matrícula. */
export interface ProcessedInvoiceDTO {
	id: number; number: string; date: string; student: string; total: number; paid: number; notes: string;
	paidWith: string[]; payments: number[]; ticketId: number | null;
}
